import React, { useEffect, useRef, useState } from "react";
import { FileText } from "lucide-react";

// Ek listesinde resimler için küçük önizleme. `false` → bugünkü simge birebir.
export const KUCUK_RESIM_ONIZLEME_ACIK = true;

const UZANTI = /\.(jpe?g|png|webp|gif)$/i;
const TURLER = ["image/jpeg", "image/jpg", "image/pjpeg", "image/png", "image/webp", "image/gif"];
const MAKS_BAYT = 25 * 1024 * 1024;
const ES_ZAMANLI = 3;
const ONBELLEK_MAKS = 200;

const resimMi = (att) => {
  if (att.size != null && att.size > MAKS_BAYT) return false;
  const ct = (att.content_type || "").toLowerCase().split(";")[0].trim();
  return TURLER.includes(ct) || UZANTI.test(att.original_filename || "");
};

// Oturum boyu önbellek: attId → blob URL | null (üretilemedi). Aynı anda en
// fazla 3 istek; böylece çok resimli görev sunucuyu/limitleyiciyi boğmaz.
const onbellek = new Map();
const bekleyen = new Map();
const kuyruk = [];
let calisan = 0;

const onbellegeYaz = (id, url) => {
  onbellek.set(id, url);
  while (onbellek.size > ONBELLEK_MAKS) {
    const [eskiId, eskiUrl] = onbellek.entries().next().value;
    onbellek.delete(eskiId);
    if (eskiUrl) URL.revokeObjectURL(eskiUrl);
  }
};

const siradaki = () => {
  while (calisan < ES_ZAMANLI && kuyruk.length) {
    const is = kuyruk.shift();
    calisan += 1;
    is().finally(() => { calisan -= 1; siradaki(); });
  }
};

const kucukResimGetir = (api, taskId, att) => {
  if (onbellek.has(att.id)) return Promise.resolve(onbellek.get(att.id));
  if (bekleyen.has(att.id)) return bekleyen.get(att.id);
  const p = new Promise((resolve) => {
    kuyruk.push(() => api.thumbnail(taskId, att.id)
      .then((res) => (res.status === 200 && res.data?.size ? URL.createObjectURL(res.data) : null))
      .catch(() => null)
      .then((url) => { onbellegeYaz(att.id, url); bekleyen.delete(att.id); resolve(url); }));
    siradaki();
  });
  bekleyen.set(att.id, p);
  return p;
};

const Simge = () => <FileText className="h-3.5 w-3.5 text-sertex-cyan/80 shrink-0" />;

export const AttachmentThumb = ({ att, taskId, api }) => {
  const aktif = KUCUK_RESIM_ONIZLEME_ACIK && typeof api?.thumbnail === "function" && resimMi(att);
  const kutuRef = useRef(null);
  // Önbellekte `null` = "üretilemedi" → hemen simge (tekrar istenmez).
  const [url, setUrl] = useState(() => (aktif && onbellek.has(att.id) ? onbellek.get(att.id) : undefined));
  const [hata, setHata] = useState(false);

  useEffect(() => {
    if (!aktif || url !== undefined || !kutuRef.current) return undefined;
    let iptal = false;
    const yukle = () => kucukResimGetir(api, taskId, att).then((u) => { if (!iptal) setUrl(u); });
    if (typeof IntersectionObserver === "undefined") { yukle(); return () => { iptal = true; }; }
    const io = new IntersectionObserver((girdiler) => {
      if (girdiler.some((g) => g.isIntersecting)) { io.disconnect(); yukle(); }
    }, { rootMargin: "120px" });
    io.observe(kutuRef.current);
    return () => { iptal = true; io.disconnect(); };
  }, [aktif, url, api, taskId, att]);

  if (!aktif) return <Simge />;
  return (
    <span
      ref={kutuRef}
      data-testid={`attach-thumb-${att.id}`}
      data-thumb-state={url && !hata ? "ok" : url === undefined ? "bekliyor" : "yok"}
      className="h-11 w-11 shrink-0 rounded overflow-hidden border border-sertex-cyan/20 bg-sertex-surface/60 grid place-items-center"
    >
      {url && !hata ? (
        <img
          src={url}
          alt=""
          draggable={false}
          onError={() => setHata(true)}
          className="h-full w-full object-cover"
        />
      ) : (
        <Simge />
      )}
    </span>
  );
};
